/**
 * Analysis Storage Service
 * Stores and retrieves analysis results from Firestore to avoid re-analyzing videos
 * Uses localStorage for instant access, Firestore for persistence
 */

import { 
  doc, 
  setDoc, 
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const ANALYSIS_COLLECTION = 'analysisResults';
const STORAGE_PREFIX = 'yt_analysis_';

/**
 * Saves analysis results - saves to localStorage instantly, Firestore in background
 * @param {string} userId - User ID
 * @param {string} videoId - YouTube video ID
 * @param {Object} analysis - Analysis results object
 * @param {Object} videoMetadata - Video metadata
 * @param {Array} comments - Array of comments
 * @returns {Promise<string>} Document ID
 */
export async function saveAnalysis(userId, videoId, analysis, videoMetadata, comments) {
  if (!userId || !videoId || !analysis) {
    throw new Error('userId, videoId, and analysis are required');
  }

  const cacheKey = `${STORAGE_PREFIX}${userId}_${videoId}`;
  const analyzedAt = Date.now();

  // Save to localStorage instantly (no await)
  try {
    const dataToCache = {
      analysis,
      videoMetadata,
      comments: comments || [],
      analyzedAt,
      cachedAt: analyzedAt,
    };
    const jsonData = JSON.stringify(dataToCache);
    localStorage.setItem(cacheKey, jsonData);
    console.log(`Ô£à [CACHE SAVE] Analysis saved to localStorage for video: ${videoId} (key: ${cacheKey}, ${(jsonData.length / 1024).toFixed(1)}KB)`);
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.warn('ÔÜá´©Å localStorage quota exceeded, clearing old cache entries...');
      // Clear old cache entries (keep last 10)
      try {
        const keys = Object.keys(localStorage).filter(k => k.startsWith(STORAGE_PREFIX));
        if (keys.length > 10) {
          keys.sort().slice(0, keys.length - 10).forEach(k => localStorage.removeItem(k));
          // Retry save
          localStorage.setItem(cacheKey, JSON.stringify({
            analysis,
            videoMetadata,
            comments: comments || [],
            analyzedAt,
            cachedAt: analyzedAt,
          }));
          console.log(`Ô£à Analysis saved to localStorage after cleanup`);
        }
      } catch (retryError) {
        console.warn('Could not save to localStorage even after cleanup:', retryError);
      }
    } else {
      console.warn('Could not save to localStorage:', error);
    }
  }

  // Save to Firestore in background (don't block)
  try {
    const docRef = doc(db, ANALYSIS_COLLECTION, `${userId}_${videoId}`);
    
    const analysisData = {
      userId,
      videoId,
      analysis,
      videoMetadata,
      comments: comments || [],
      analyzedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Don't await - let it save in background
    setDoc(docRef, analysisData, { merge: true }).then(() => {
      console.log(`Ô£à Analysis saved to Firestore for video: ${videoId}`);
    }).catch((error) => {
      console.error('Error saving analysis to Firestore:', error);
      // Don't throw - localStorage is the primary cache
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error initiating Firestore save:', error);
    // Don't throw - localStorage save succeeded
    return `${userId}_${videoId}`;
  }
}

/**
 * Loads cached analysis results - checks localStorage first (SYNCHRONOUS, instant), then Firestore
 * @param {string} userId - User ID
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<Object|null>} Analysis data or null if not found
 */
export async function loadAnalysis(userId, videoId) {
  if (!userId || !videoId) {
    return null;
  }

  const cacheKey = `${STORAGE_PREFIX}${userId}_${videoId}`;

  // CRITICAL: Check localStorage SYNCHRONOUSLY first (instant, no async overhead)
  // localStorage.getItem is synchronous, so this should be microseconds
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const data = JSON.parse(cached);
      // Check if cache is less than 7 days old
      const cacheAge = Date.now() - (data.cachedAt || 0);
      const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
      
      if (cacheAge < maxAge && data.analysis && data.videoMetadata) {
        // Cache found! Return immediately (should be < 1ms)
        console.log(`Ô£à [CACHE HIT] Loaded from localStorage for ${videoId} (${(cacheAge / 1000 / 60).toFixed(1)}min old, analysis: ${!!data.analysis}, metadata: ${!!data.videoMetadata}, comments: ${data.comments?.length || 0})`);
        return {
          analysis: data.analysis,
          videoMetadata: data.videoMetadata,
          comments: data.comments || [],
          analyzedAt: data.analyzedAt,
        };
      } else {
        // Cache expired - remove it
        console.log(`ÔÜá´©Å [CACHE EXPIRED] Removing expired cache for ${videoId} (${(cacheAge / 1000 / 60 / 60 / 24).toFixed(1)} days old)`);
        localStorage.removeItem(cacheKey);
      }
    } else {
      console.log(`ÔÜá´©Å [CACHE MISS] No localStorage cache found for ${videoId} (key: ${cacheKey})`);
    }
  } catch (error) {
    // If localStorage fails, continue to Firestore fallback
    console.warn(`ÔØî [CACHE ERROR] Error reading localStorage for ${videoId}:`, error);
  }

  // Fallback to Firestore (slower but persistent)
  try {
    const docRef = doc(db, ANALYSIS_COLLECTION, `${userId}_${videoId}`);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Convert timestamps
      let analyzedAt = null;
      if (data.analyzedAt) {
        if (typeof data.analyzedAt.toMillis === 'function') {
          analyzedAt = data.analyzedAt.toMillis();
        } else if (typeof data.analyzedAt.toDate === 'function') {
          analyzedAt = data.analyzedAt.toDate().getTime();
        } else if (data.analyzedAt instanceof Date) {
          analyzedAt = data.analyzedAt.getTime();
        } else if (typeof data.analyzedAt === 'number') {
          analyzedAt = data.analyzedAt;
        }
      }

      const result = {
        analysis: data.analysis,
        videoMetadata: data.videoMetadata,
        comments: data.comments || [],
        analyzedAt,
      };

      // Also save to localStorage for next time (async, don't block)
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          ...result,
          cachedAt: Date.now(),
        }));
      } catch (storageError) {
        // Ignore localStorage errors - Firestore is the source of truth
      }

      return result;
    }

    return null;
  } catch (error) {
    console.error('Error loading analysis from Firestore:', error);
    return null;
  }
}

/**
 * Checks if cached analysis exists for a video
 * @param {string} userId - User ID
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<boolean>} True if cached analysis exists
 */
export async function hasCachedAnalysis(userId, videoId) {
  if (!userId || !videoId) {
    return false;
  }

  try {
    const docRef = doc(db, ANALYSIS_COLLECTION, `${userId}_${videoId}`);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  } catch (error) {
    console.error('Error checking cached analysis:', error);
    return false;
  }
}

/**
 * Deletes cached analysis for a video
 * @param {string} userId - User ID
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<void>}
 */
export async function deleteAnalysis(userId, videoId) {
  if (!userId || !videoId) {
    throw new Error('userId and videoId are required');
  }

  try {
    const docRef = doc(db, ANALYSIS_COLLECTION, `${userId}_${videoId}`);
    const { deleteDoc } = await import('firebase/firestore');
    await deleteDoc(docRef);
    console.log(`Ô£à Analysis deleted for video: ${videoId}`);
  } catch (error) {
    console.error('Error deleting analysis:', error);
    throw error;
  }
}

